import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import PageShell from "../components/PageShell";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Loader2, Briefcase, Users, Star, MessageSquare, MapPin, Building2, Target,
  Bookmark, FileText, UserPlus, Check, X, Plus,
} from "lucide-react";
import { toast } from "sonner";

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tint}`}><Icon className="h-5 w-5" /></div>
    <p className="mt-3 font-heading text-3xl font-extrabold text-[#0B2545]">{value}</p>
    <p className="text-sm text-slate-500">{label}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    applied: "bg-slate-100 text-slate-600",
    shortlisted: "bg-[#16A34A]/10 text-[#16A34A]",
    declined: "bg-red-100 text-red-600",
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${map[status] || map.applied}`}>{status}</span>;
};

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [authLoading, user, navigate]);

  const load = () => {
    if (!user) return;
    const url = user.role === "organisation" ? "/org/dashboard" : "/pro/dashboard";
    api.get(url).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const message = async (otherUserId, name) => {
    if (!otherUserId) { toast.error(`${name || "This user"} isn't reachable for messaging (demo profile).`); return; }
    try {
      const r = await api.post("/conversations/start", { user_id: otherUserId });
      navigate(`/messages/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start conversation");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}/status`, { status });
      toast.success(status === "shortlisted" ? "Shortlisted" : status === "declined" ? "Declined" : "Updated");
      load();
    } catch {
      toast.error("Could not update status");
    }
  };

  if (authLoading || !user || loading)
    return <PageShell><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#1E4FA3]" /></div></PageShell>;

  const isOrg = user.role === "organisation";
  const s = data?.stats || {};

  return (
    <PageShell>
      <div className="mx-auto max-w-[1100px] px-6 py-10 lg:px-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-heading text-[32px] font-extrabold text-[#0B2545] sm:text-[38px]">Welcome back, {user.name.split(" ")[0]}</h1>
            <p className="mt-1 text-[16px] text-slate-600">{isOrg ? "Manage your requirements and applicants." : "Track your applications and conversations."}</p>
          </div>
          {isOrg ? (
            <Button onClick={() => navigate("/post-requirement")} className="h-11 shrink-0 rounded-xl bg-[#1E4FA3] px-5 font-semibold text-white hover:bg-[#193f83]"><Plus className="mr-1.5 h-4 w-4" /> Post a Requirement</Button>
          ) : (
            <Button onClick={() => navigate("/tell-us")} variant="outline" className="h-11 shrink-0 rounded-xl border-2 border-slate-200 px-5 font-semibold text-slate-700 hover:bg-slate-50"><Target className="mr-1.5 h-4 w-4" /> Edit Preferences</Button>
          )}
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {isOrg ? (
            <>
              <StatCard icon={Briefcase} label="Requirements" value={s.postings || 0} tint="bg-[#1E4FA3]/10 text-[#1E4FA3]" />
              <StatCard icon={FileText} label="Applications" value={s.applications || 0} tint="bg-[#16A34A]/10 text-[#16A34A]" />
              <StatCard icon={Star} label="Shortlisted" value={s.shortlisted || 0} tint="bg-amber-100 text-amber-600" />
              <StatCard icon={UserPlus} label="Connections" value={s.connections || 0} tint="bg-purple-100 text-purple-600" />
            </>
          ) : (
            <>
              <StatCard icon={FileText} label="Applications" value={s.applications || 0} tint="bg-[#1E4FA3]/10 text-[#1E4FA3]" />
              <StatCard icon={Star} label="Shortlisted" value={s.shortlisted || 0} tint="bg-amber-100 text-amber-600" />
              <StatCard icon={Bookmark} label="Saved" value={s.saved || 0} tint="bg-[#16A34A]/10 text-[#16A34A]" />
              <StatCard icon={MessageSquare} label="Conversations" value={s.conversations || 0} tint="bg-purple-100 text-purple-600" />
            </>
          )}
        </div>

        {/* Content */}
        <div className="mt-8">
          {isOrg ? <OrgTabs data={data} navigate={navigate} message={message} setStatus={setStatus} /> : <ProTabs data={data} navigate={navigate} message={message} />}
        </div>
      </div>
    </PageShell>
  );
};

const Empty = ({ text, cta, onCta }) => (
  <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-12 text-center">
    <p className="text-slate-500">{text}</p>
    {cta && <Button onClick={onCta} className="mt-4 h-10 rounded-xl bg-[#1E4FA3] px-5 font-semibold text-white hover:bg-[#193f83]">{cta}</Button>}
  </div>
);

const OrgTabs = ({ data, navigate, message, setStatus }) => (
  <Tabs defaultValue="applications">
    <TabsList className="mb-5">
      <TabsTrigger value="applications">Applications</TabsTrigger>
      <TabsTrigger value="requirements">Requirements</TabsTrigger>
      <TabsTrigger value="connections">Connections</TabsTrigger>
    </TabsList>

    <TabsContent value="applications">
      {(!data.applications || data.applications.length === 0) ? (
        <Empty text="No applications yet. Post a requirement to start receiving them." cta="Post a Requirement" onCta={() => navigate("/post-requirement")} />
      ) : (
        <div className="space-y-3">
          {data.applications.map((a) => {
            const pro = a.professional || {};
            return (
              <div key={a.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#16A34A]/10 font-heading text-sm font-bold text-[#16A34A]">{initials(a.professional_name)}</div>
                    <div>
                      <h3 className="font-heading text-[16px] font-bold text-[#0B2545]">{a.professional_name}</h3>
                      {pro.title && <p className="text-sm text-slate-600">{pro.title}</p>}
                      <p className="mt-1 text-sm text-slate-500">Applied for <span className="font-medium text-slate-700">{a.job_title}</span></p>
                      {pro.preferences?.desired_role && <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500"><Target className="h-3.5 w-3.5 text-[#16A34A]" />Seeking: {pro.preferences.desired_role}</p>}
                    </div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button onClick={() => setStatus(a.id, "shortlisted")} disabled={a.status === "shortlisted"} className="h-9 rounded-lg bg-[#16A34A] px-3 text-sm font-semibold text-white hover:bg-[#138a3f] disabled:opacity-50"><Check className="mr-1 h-4 w-4" />Shortlist</Button>
                  <Button onClick={() => setStatus(a.id, "declined")} variant="outline" className="h-9 rounded-lg border-2 border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:border-red-300 hover:text-red-600"><X className="mr-1 h-4 w-4" />Decline</Button>
                  <Button onClick={() => message(a.professional_user_id, a.professional_name)} variant="outline" className="h-9 rounded-lg border-2 border-[#1E4FA3]/25 px-3 text-sm font-semibold text-[#1E4FA3] hover:bg-[#1E4FA3]/5"><MessageSquare className="mr-1 h-4 w-4" />Message</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </TabsContent>

    <TabsContent value="requirements">
      {(!data.postings || data.postings.length === 0) ? (
        <Empty text="You haven't posted any requirements yet." cta="Post a Requirement" onCta={() => navigate("/post-requirement")} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {data.postings.map((j) => (
            <div key={j.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-heading text-[17px] font-bold text-[#0B2545]">{j.role_title}</h3>
                <span className="rounded-full bg-[#1E4FA3]/10 px-2.5 py-1 text-xs font-semibold text-[#1E4FA3]">{j.applicant_count} applicant{j.applicant_count === 1 ? "" : "s"}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-500">
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{j.location}</span>
                <span className="flex items-center gap-1"><Briefcase className="h-4 w-4" />{j.employment_type}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </TabsContent>

    <TabsContent value="connections">
      {(!data.connections || data.connections.length === 0) ? (
        <Empty text="No connections yet. Browse professionals and connect with them." cta="Browse Professionals" onCta={() => navigate("/professionals")} />
      ) : (
        <div className="space-y-3">
          {data.connections.map((c) => {
            const pro = c.professional || {};
            return (
              <div key={c.id} className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#16A34A]/10 font-heading text-sm font-bold text-[#16A34A]">{initials(c.professional_name)}</div>
                  <div>
                    <h3 className="font-heading text-[16px] font-bold text-[#0B2545]">{c.professional_name}</h3>
                    {pro.title && <p className="text-sm text-slate-600">{pro.title}</p>}
                  </div>
                </div>
                <Button onClick={() => message(c.professional_user_id, c.professional_name)} variant="outline" className="h-9 shrink-0 rounded-lg border-2 border-[#1E4FA3]/25 px-3 text-sm font-semibold text-[#1E4FA3] hover:bg-[#1E4FA3]/5"><MessageSquare className="mr-1 h-4 w-4" />Message</Button>
              </div>
            );
          })}
        </div>
      )}
    </TabsContent>
  </Tabs>
);

const JobRow = ({ a, message }) => {
  const job = a.job || {};
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="font-heading text-[16px] font-bold text-[#0B2545]">{job.role_title || a.job_title}</h3>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-500">
            {job.organisation_name && <span className="flex items-center gap-1"><Building2 className="h-4 w-4" />{job.organisation_name}</span>}
            {job.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{job.location}</span>}
          </div>
        </div>
        <StatusBadge status={a.status} />
      </div>
      <div className="mt-4">
        <Button onClick={() => message(a.org_user_id, job.organisation_name)} variant="outline" className="h-9 rounded-lg border-2 border-[#1E4FA3]/25 px-3 text-sm font-semibold text-[#1E4FA3] hover:bg-[#1E4FA3]/5"><MessageSquare className="mr-1 h-4 w-4" />Message organisation</Button>
      </div>
    </div>
  );
};

const ProTabs = ({ data, navigate, message }) => (
  <Tabs defaultValue="applications">
    <TabsList className="mb-5">
      <TabsTrigger value="applications">My Applications</TabsTrigger>
      <TabsTrigger value="shortlisted">Shortlisted</TabsTrigger>
    </TabsList>

    <TabsContent value="applications">
      {(!data.applications || data.applications.length === 0) ? (
        <Empty text="You haven't applied to any roles yet." cta="Find Opportunities" onCta={() => navigate("/opportunities")} />
      ) : (
        <div className="space-y-3">
          {data.applications.map((a) => <JobRow key={a.id} a={a} message={message} />)}
        </div>
      )}
    </TabsContent>

    <TabsContent value="shortlisted">
      {(!data.shortlisted || data.shortlisted.length === 0) ? (
        <Empty text="No shortlisted openings yet. Organisations will shortlist you here." />
      ) : (
        <div className="space-y-3">
          {data.shortlisted.map((a) => (
            <div key={a.id} className="rounded-2xl border-2 border-[#16A34A]/30 bg-[#16A34A]/5 p-5 shadow-sm">
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-[#16A34A]/15 px-2.5 py-1 text-xs font-semibold text-[#16A34A]"><Star className="h-3.5 w-3.5" />Shortlisted</div>
              <JobRowInner a={a} message={message} />
            </div>
          ))}
        </div>
      )}
    </TabsContent>
  </Tabs>
);

const JobRowInner = ({ a, message }) => {
  const job = a.job || {};
  return (
    <div>
      <h3 className="font-heading text-[16px] font-bold text-[#0B2545]">{job.role_title || a.job_title}</h3>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-600">
        {job.organisation_name && <span className="flex items-center gap-1"><Building2 className="h-4 w-4 text-slate-400" />{job.organisation_name}</span>}
        {job.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4 text-slate-400" />{job.location}</span>}
      </div>
      <Button onClick={() => message(a.org_user_id, job.organisation_name)} variant="outline" className="mt-4 h-9 rounded-lg border-2 border-[#1E4FA3]/25 bg-white px-3 text-sm font-semibold text-[#1E4FA3] hover:bg-[#1E4FA3]/5"><MessageSquare className="mr-1 h-4 w-4" />Message organisation</Button>
    </div>
  );
};

export default Dashboard;
